import { useState, useRef, useEffect, useCallback } from "react";
import { FiUser, FiCheck, FiClock, FiX, FiMail, FiActivity, FiMoreVertical, FiAlertTriangle } from "react-icons/fi";
import { cn, showToast } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import Modal from "newui/components/ui/Modal/Modal";
import {
	usePanelMemberStatus,
	useRevokeMember,
	useUnrevokeMember,
	useSendReminder,
	useResendMemberInvite,
} from "../../../../hooks/usePanelScoringQueries";
import type { PanelMemberWithProgress, PanelMemberStatus, PanelMemberRole } from "../../../../types/panel.type";

interface PanelMemberListProps {
	rfxId: number;
	evalId: number;
}

interface MemberActionsMenuProps {
	rfxId: number;
	evalId: number;
	member: PanelMemberWithProgress;
}

const getStatusIcon = (status: PanelMemberStatus): React.ReactNode => {
	switch (status) {
		case "submitted":
			return <FiCheck size={12} aria-hidden="true" />;
		case "accepted":
		case "opened":
		case "in_progress":
			return <FiActivity size={12} aria-hidden="true" />;
		case "invited":
			return <FiMail size={12} aria-hidden="true" />;
		case "declined":
		case "revoked":
			return <FiX size={12} aria-hidden="true" />;
		default:
			return <FiClock size={12} aria-hidden="true" />;
	}
};

const getStatusBadgeClasses = (status: PanelMemberStatus): string => {
	switch (status) {
		case "submitted":
			return "bg-green-100 text-green-700";
		case "accepted":
		case "opened":
			return "bg-[#e8eeff] text-[#0031DD]";
		case "in_progress":
			return "bg-[#e8eeff] text-[#0031DD]";
		case "invited":
			return "bg-amber-100 text-amber-700";
		case "declined":
			return "bg-red-100 text-red-700";
		case "revoked":
			return "bg-gray-200 text-gray-500";
		default:
			return "bg-gray-100 text-gray-600";
	}
};

const getStatusLabel = (status: PanelMemberStatus): string => {
	switch (status) {
		case "submitted":
			return "Submitted";
		case "accepted":
			return "Accepted";
		case "opened":
			return "Opened";
		case "in_progress":
			return "In Progress";
		case "invited":
			return "Invited";
		case "declined":
			return "Declined";
		case "revoked":
			return "Revoked";
		case "pending":
			return "Pending";
		default:
			return status;
	}
};

const getRoleBadgeClasses = (role: PanelMemberRole): string => {
	switch (role) {
		case "chair":
			return "bg-purple-100 text-purple-700";
		case "member":
			return "bg-gray-100 text-gray-700";
		case "observer":
			return "bg-amber-50 text-amber-600";
		default:
			return "bg-gray-100 text-gray-700";
	}
};

const formatDate = (dateStr: string | null): string => {
	if (!dateStr) {
		return "—";
	}
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const MemberActionsMenu: React.FC<MemberActionsMenuProps> = ({
	rfxId,
	evalId,
	member,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	const resendInviteMutation = useResendMemberInvite(rfxId, evalId);
	const revokeMutation = useRevokeMember(rfxId, evalId);
	const unrevokeMutation = useUnrevokeMember(rfxId, evalId);
	const reminderMutation = useSendReminder(rfxId, evalId);

	const isRevoked = member.status === "revoked";
	const canResendInvite = member.status === "invited";
	const canSendReminder = !["submitted", "declined", "revoked"].includes(member.status);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const handleResendInvite = useCallback(() => {
		setIsOpen(false);
		resendInviteMutation.mutate(member.id, {
			onSuccess: () => {
				showToast("Invitation resent successfully.", "Invite Resent", true);
			},
			onError: () => {
				showToast("Failed to resend invitation.", "Error", false);
			},
		});
	}, [member.id, resendInviteMutation]);

	const handleRevokeClick = useCallback(() => {
		setIsOpen(false);
		setShowRevokeConfirm(true);
	}, []);

	const handleConfirmRevoke = useCallback(() => {
		setShowRevokeConfirm(false);
		revokeMutation.mutate(member.id, {
			onSuccess: () => {
				showToast("Member access has been revoked.", "Access Revoked", true);
			},
			onError: () => {
				showToast("Failed to revoke access.", "Error", false);
			},
		});
	}, [member.id, revokeMutation]);

	const handleUnrevoke = useCallback(() => {
		setIsOpen(false);
		unrevokeMutation.mutate(member.id, {
			onSuccess: () => {
				showToast("Member access has been restored.", "Access Restored", true);
			},
			onError: () => {
				showToast("Failed to restore access.", "Error", false);
			},
		});
	}, [member.id, unrevokeMutation]);

	const handleSendReminder = useCallback(() => {
		setIsOpen(false);
		reminderMutation.mutate(member.id, {
			onSuccess: () => {
				showToast("Reminder sent successfully.", "Reminder Sent", true);
			},
			onError: () => {
				showToast("Failed to send reminder.", "Error", false);
			},
		});
	}, [member.id, reminderMutation]);

	return (
		<>
			<div className="relative" ref={menuRef}>
				<button
					type="button"
					onClick={() => setIsOpen(!isOpen)}
					className="inline-flex items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-1"
					aria-label={`Actions for ${member.name || member.email}`}
					aria-haspopup="true"
					aria-expanded={isOpen}
					data-testid={`member-actions-trigger-${member.id}`}
				>
					<FiMoreVertical size={16} />
				</button>
				{isOpen && (
					<div
						className="absolute right-0 top-full z-10 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
						role="menu"
						data-testid={`member-actions-menu-${member.id}`}
					>
						<button
							type="button"
							role="menuitem"
							onClick={handleResendInvite}
							disabled={!canResendInvite}
							className={cn(
								"flex w-full items-center px-3 py-2 text-left text-sm transition-colors",
								canResendInvite
									? "text-gray-700 hover:bg-gray-50"
									: "text-gray-300 cursor-not-allowed"
							)}
							data-testid={`member-action-resend-${member.id}`}
						>
							Resend Invite
						</button>
						{isRevoked ? (
							<button
								type="button"
								role="menuitem"
								onClick={handleUnrevoke}
								className="flex w-full items-center px-3 py-2 text-left text-sm text-green-600 hover:bg-green-50 transition-colors"
								data-testid={`member-action-unrevoke-${member.id}`}
							>
								Unrevoke Access
							</button>
						) : (
							<button
								type="button"
								role="menuitem"
								onClick={handleRevokeClick}
								className="flex w-full items-center px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
								data-testid={`member-action-revoke-${member.id}`}
							>
								Revoke Access
							</button>
						)}
						<button
							type="button"
							role="menuitem"
							onClick={handleSendReminder}
							disabled={!canSendReminder}
							className={cn(
								"flex w-full items-center px-3 py-2 text-left text-sm transition-colors",
								canSendReminder
									? "text-gray-700 hover:bg-gray-50"
									: "text-gray-300 cursor-not-allowed"
							)}
							data-testid={`member-action-remind-${member.id}`}
						>
							Send Reminder
						</button>
					</div>
				)}
			</div>

			{showRevokeConfirm && (
				<Modal
					isOpen={showRevokeConfirm}
					onClose={() => setShowRevokeConfirm(false)}
					title="Revoke Member Access"
					size="md"
					iconType="warning"
					modalType="standard"
					closeOnEsc
					closeOnOverlayClick={false}
					primaryButton={{
						text: "Revoke Access",
						onClick: handleConfirmRevoke,
						width: "full",
					}}
					secondaryButton={{
						text: "Cancel",
						onClick: () => setShowRevokeConfirm(false),
						width: "full",
					}}
				>
					<div className="space-y-3">
						<p className="text-sm text-gray-600 text-center">
							Are you sure you want to revoke access for <strong>{member.name || member.email}</strong>?
						</p>
						<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
							<div className="flex gap-2">
								<FiAlertTriangle size={16} className="shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
								<div className="text-sm text-amber-700 space-y-1">
									<p>This member will no longer be able to submit or modify their scores.</p>
									<p>Any scores they have already entered will be excluded from the evaluation averages and comparison data.</p>
								</div>
							</div>
						</div>
					</div>
				</Modal>
			)}
		</>
	);
};

const MemberRow: React.FC<{
	member: PanelMemberWithProgress;
	rfxId: number;
	evalId: number;
	showActions: boolean;
}> = ({ member, rfxId, evalId, showActions }) => {
	const progressPercent = Math.min(Math.max(member.progress, 0), 100);

	return (
		<tr
			className="border-b border-gray-100 transition-colors hover:bg-gray-50"
			data-testid={`panel-member-row-${member.id}`}
		>
			<td className="px-3 py-3">
				<div className="flex items-center gap-2">
					<div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-500">
						<FiUser size={14} aria-hidden="true" />
					</div>
					<div>
						<p className="font-medium text-gray-900 text-sm">
							{member.name || member.email}
							{member.role === "chair" && (
								<span className="ml-1 text-xs font-normal text-gray-400">(you)</span>
							)}
						</p>
						{member.name && (
							<p className="text-xs text-gray-500">{member.email}</p>
						)}
					</div>
				</div>
			</td>
			<td className="px-3 py-3">
				<span
					className={cn(
						"inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
						getRoleBadgeClasses(member.role)
					)}
				>
					{member.role === "chair" ? "RFx Lead" : member.role}
				</span>
			</td>
			<td className="px-3 py-3">
				<span
					className={cn(
						"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
						getStatusBadgeClasses(member.status)
					)}
				>
					{getStatusIcon(member.status)}
					{getStatusLabel(member.status)}
				</span>
			</td>
			<td className="px-3 py-3">
				<div className="flex items-center gap-2">
					<div className="h-2 w-20 rounded-full bg-gray-200" role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100}>
						<div
							className={cn(
								"h-full rounded-full transition-all",
								progressPercent === 100
									? "bg-green-500"
									: progressPercent > 0
										? "bg-[#0031DD]"
										: "bg-gray-200"
							)}
							style={{ width: `${progressPercent}%` }}
						/>
					</div>
					<span className="text-xs text-gray-500 tabular-nums">
						{progressPercent}%
					</span>
				</div>
			</td>
			<td className="px-3 py-3 text-xs text-gray-500">
				{member.submitted_at
					? formatDate(member.submitted_at)
					: "—"}
			</td>
			{showActions && (
				<td className="px-3 py-3">
					{member.role !== "chair" ? (
						<MemberActionsMenu
							rfxId={rfxId}
							evalId={evalId}
							member={member}
						/>
					) : null}
				</td>
			)}
		</tr>
	);
};

export const PanelMemberList: React.FC<PanelMemberListProps> = ({
	rfxId,
	evalId,
}) => {
	const { data: members, isLoading, error } = usePanelMemberStatus(rfxId, evalId);

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="panel-member-list-loading">
				{[1, 2, 3].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (error) {
		return (
			<p className="text-sm text-red-600" data-testid="panel-member-list-error">
				Failed to load panel members.
			</p>
		);
	}

	const activeMembers = (members ?? []).filter((m) => !m.is_removed);

	if (activeMembers.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm"
				data-testid="panel-member-list-empty"
			>
				No panel members have been added to this evaluation.
			</div>
		);
	}

	const submittedCount = activeMembers.filter((m) => m.status === "submitted").length;
	const hasNonChairMembers = activeMembers.some((m) => m.role !== "chair");

	return (
		<div data-testid="panel-member-list">
			<div className="mb-3 flex items-center justify-between">
				<h4 className="text-sm font-medium text-gray-700">
					Panel Members
				</h4>
				<span className="text-xs text-gray-500">
					{submittedCount} of {activeMembers.length} submitted
				</span>
			</div>
			<div className="w-full overflow-visible">
				<table className="w-full min-w-[600px] border-collapse text-sm">
					<thead>
						<tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
							<th className="px-3 py-3">Member</th>
							<th className="px-3 py-3">Role</th>
							<th className="px-3 py-3">Status</th>
							<th className="px-3 py-3">Progress</th>
							<th className="px-3 py-3">Submitted</th>
							{hasNonChairMembers && (
								<th className="px-3 py-3 w-12">Actions</th>
							)}
						</tr>
					</thead>
					<tbody>
						{activeMembers.map((member) => (
							<MemberRow
								key={member.id}
								member={member}
								rfxId={rfxId}
								evalId={evalId}
								showActions={hasNonChairMembers}
							/>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};
